import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';

import {
  removeOneAnnouncementFromStore,
  addAnnouncementSearchResultsToStore
} from 'store/actions/announcement';
import { setPageTitle } from 'store/actions/header';
import { Button } from 'app/shared/button';
import GmModal from 'app/shared/modal/modal';
import AddAnnouncement from '../create/create';
import UpdateAnnouncement from '../update/update';
import styles from '../announcements.module.css';
import { useAnnouncementService } from 'hooks/api/iam/announcement';

const AnnouncementList = () => {
  const dispatch = useDispatch();
  const { deleteAnnouncement, fetchAnnouncements } = useAnnouncementService();
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const announcement_in_store = useSelector((state) => state.announcements);

  const [announcements, setAnnouncements] = useState([]);
  const [total_announcements, setTotalAnnouncements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [show_creation_modal, setShowCreationModal] = useState(false);
  const [show_update_modal, setShowUpdateModal] = useState(false);
  const announcement_data = useRef(null);

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Announcements', path: '.' }]));
    handleDataRequest(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAnnouncements(Object.values(announcement_in_store));
  }, [announcement_in_store]);

  const config = {
    actions: {
      single: () => [
        [
          { label: 'Edit', value: 'edit' },
          { label: 'Delete', value: 'delete' }
        ]
      ]
    },
    css: {},
    fields: [
      {
        title: 'Preview',
        key: 'image_url',
        formatter: (value) => <img src={value} alt="" className={styles.previewThumb} />
      },
      {
        title: 'Image URL',
        key: 'image_url',
        searchable: true,
        isTitle: true
      },
      {
        title: 'Target URL',
        key: 'url',
        formatter: (value) => value || '—',
        isTagline: true
      }
    ],
    items: announcements,
    search_key: 'image_url',
    search_text: 'Search announcements'
  };

  const handleDatatableAction = (payload) => {
    const { name, type, data } = payload;
    if (type !== 'single') return;

    if (name === 'edit') {
      announcement_data.current = data;
      setShowUpdateModal(true);
    }
    if (name === 'delete') handleSingleDeletion(data.id);
  };

  const handleSingleDeletion = async (announcement_id) => {
    const result = await deleteAnnouncement(announcement_id);
    if (result) dispatch(removeOneAnnouncementFromStore(announcement_id));
  };

  const handleDataRequest = async (page = 0, population = 10) => {
    try {
      setLoading(true);
      const { announcements, size } = await fetchAnnouncements({
        query_string: `page=${page}&population=${population}&sort_by=-created_on`
      });

      dispatch(addAnnouncementSearchResultsToStore(announcements));
      setTotalAnnouncements(size);
    } catch (e) {
      dispatch(addAnnouncementSearchResultsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const table_actions = (
    <Button icon_name="add" text="Announcement" onClick={() => setShowCreationModal(true)} />
  );

  return (
    <div>
      {is_mobile_view ? (
        <MobileDatatable
          config={config}
          action={handleDatatableAction}
          onClick={() => {}}
          onDataRequest={handleDataRequest}
        />
      ) : (
        <WebDatatable
          config={{
            ...config,
            total_count: total_announcements
          }}
          action={handleDatatableAction}
          onClick={() => {}}
          loading_data={loading}
          table_actions={table_actions}
          onDataRequest={handleDataRequest}
        />
      )}

      <GmModal
        bodyClassName={styles.announcementModal}
        title="Add Announcement"
        show_title
        show_modal={show_creation_modal}
        onClose={() => setShowCreationModal(false)}
      >
        <AddAnnouncement closeModal={() => setShowCreationModal(false)} />
      </GmModal>

      <GmModal
        bodyClassName={styles.announcementModal}
        title="Update Announcement"
        show_title
        show_modal={show_update_modal}
        onClose={() => setShowUpdateModal(false)}
      >
        <UpdateAnnouncement
          announcement={announcement_data.current}
          closeModal={() => setShowUpdateModal(false)}
        />
      </GmModal>
    </div>
  );
};

export default AnnouncementList;
